import { useState, useEffect, useRef } from 'react';
import { useQuery } from 'react-apollo';

import { albumMembershipGroupExists } from '../../../../hooks/useAlbumMembershipGroup/graphql';

/**
 * Waits for the album membership group of a hierarchy item to exist, polling
 * until it does or until the maximum polling time elapsed. The group is written
 * asynchronously once an identity is linked to the account, so the views it
 * feeds have to be held back until it is actually there.
 *
 * @param {Object} opts
 * @param {String} opts.entityId The hierarchy item to wait on.
 * @param {Boolean} opts.enabled Whether there is anything to wait for.
 * @param {Number} [opts.pollIntervalMs] The polling interval (in MS).
 * @param {Number} [opts.maxPollingTimeMs] The maximum polling time (in MS). If
 *   0 is passed, polling can be done indefinitely.
 *
 * @returns {{ ready: Boolean, waiting: Boolean }} Whether the group exists and
 *   whether it is still being waited on. Both are false once the wait timed
 *   out, so callers fall back to their default behaviour.
 */
const useAlbumMembershipGroupWait = ({
  entityId,
  enabled,
  pollIntervalMs = 1000,
  maxPollingTimeMs = 30000,
}) => {
  const maxPollingTimer = useRef(null);
  const [timedOut, setTimedOut] = useState(false);

  const {
    data: { albumMembershipGroup } = {},
    startPolling,
    stopPolling,
  } = useQuery(albumMembershipGroupExists, {
    variables: {
      id: entityId,
      root_groups_only: true,
      include_matched_photos_only: true,
    },
    // The group is being written while this polls, so a cached miss would
    // never resolve.
    fetchPolicy: 'network-only',
    skip: !enabled || !entityId,
  });

  const ready = !!albumMembershipGroup?.id;
  // Derived rather than held in state so that a caller reading this in the same
  // commit that enables the wait already sees it, instead of redirecting away
  // before the state update lands.
  const waiting = !!enabled && !!entityId && !ready && !timedOut;

  const clearMaxPollingTimer = () => {
    if (maxPollingTimer.current) {
      clearTimeout(maxPollingTimer.current);
      maxPollingTimer.current = null;
    }
  };

  useEffect(() => {
    if (!waiting) {
      clearMaxPollingTimer();

      try {
        stopPolling();
      } catch {
        // Do nothing, the query may not be polling.
      }

      return;
    }

    startPolling(pollIntervalMs);

    if (maxPollingTimeMs && !maxPollingTimer.current) {
      maxPollingTimer.current = setTimeout(
        () => setTimedOut(true),
        maxPollingTimeMs,
      );
    }
  }, [waiting]);

  useEffect(() => () => clearMaxPollingTimer(), []);

  return { ready, waiting };
};

export default useAlbumMembershipGroupWait;
